import React, { useContext } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { IconName } from '@fortawesome/fontawesome-svg-core'
// evertel
import { Icon, Text, Badge, Col } from '@evertel/web/ui'
import { RoomContext } from '../../RoomContext'
import { useService } from '@evertel/di'
import { UnreadCountsState } from '@evertel/blue-user'

interface RightGutterProps {
    toggleAside: (option: string, intent: string) => void
    activeTab?: string
    roomNotificationsEnabled?: boolean
}


const RightGutter: React.FC<RightGutterProps> = observer(({
    toggleAside,
    activeTab,
    roomNotificationsEnabled,
    ...otherProps
}) => {

    const { setIsMessageSearchModalOpen, roomJoinRequestsController, roomController } = useContext(RoomContext)
    const unreadState = useService(UnreadCountsState, [])

    const showOption = (option: string) => {
        toggleAside(option, 'open')
    }

    const roomUnreads = unreadState.getRoomUnread(roomController.roomId)
    const repliesHaveUrgent = unreadState.getRepliesUrgentCount(roomController.roomId)

    return (
        <Col
            valign="top"
            className="right-gutter"
            {...otherProps}>
            <GutterItem
                id="search"
                onClick={() => setIsMessageSearchModalOpen(true)}
                icon="search"
                title="Search"
                ariaLabel="Search messages in the room"
                activeTab={activeTab}
            />
            <GutterItem
                id="replies"
                onClick={() => showOption('replies')}
                icon="reply"
                title="Replies"
                ariaLabel="View replies in the room"
                activeTab={activeTab}
                badgeCount={roomUnreads?.repliesCount ?? 0}
                badgeColor={`${repliesHaveUrgent ? 'danger': 'warning'}`}
            />
            <GutterItem
                id="directory"
                onClick={() => showOption('directory')}
                icon="users"
                title="Members"
                ariaLabel="View members in the room"
                activeTab={activeTab}
                badgeCount={roomJoinRequestsController.pendingCount}
                badgeColor="danger"
            />
            <GutterItem
                id="media-search"
                onClick={() => showOption('media-search')}
                icon="file-alt"
                title="Files"
                ariaLabel="Search files in the room"
                activeTab={activeTab}
            />
            <GutterItem
                id="settings"
                onClick={() => showOption('settings')}
                icon="cog"
                title="Settings"
                ariaLabel="See or edit Room Settings"
                activeTab={activeTab}
            />
        </Col>
    )
})

interface ItemProps {
    id: string
    onClick: () => void
    icon: IconName
    title: string
    ariaLabel: string
    badgeCount?: number
    badgeColor?: string
    activeTab?: string
    color?: string
}

const GutterItem: React.FC<ItemProps> = observer(({
    id,
    onClick,
    icon,
    title,
    ariaLabel,
    badgeCount = 0,
    badgeColor = 'primary',
    activeTab,
    color = ''
}) => {
    const _classNames = classNames(
        'right-gutter-items',
        { 'active': activeTab === id }
    )

    const isExpanded = activeTab === id
    const panelId = `slidepanel-${id}`

    return (
        <button
            className={_classNames}
            onClick={onClick}
            aria-expanded={isExpanded}
            aria-controls={panelId}
            aria-haspopup="true"
            aria-label={`${ariaLabel}${badgeCount > 0 ? `, ${badgeCount} unread` : ''}`}
        >
            <div className="position-relative">
                <Icon
                    name={icon}
                    color={color}
                    aria-hidden="true"
                />
                { (badgeCount > 0) &&
                    <Badge
                        color={badgeColor}
                        size={10}
                        shape="rounded-pill"
                        className="position-absolute translate-middle"
                        aria-hidden="true"
                    >
                        {<strong>{(badgeCount > 200) ? '200+' : badgeCount}</strong>}
                    </Badge>
                }
            </div>
            <Text
                color={color}>
                {title}
            </Text>
        </button>
    )
})

export { RightGutter }
